import { normalizePrice } from './format'
import { offerTotal, type TyreOffer, type TyreTier } from './tyreOffers'

/**
 * Quantity-based tyre offers — "buy 2, save 10%".
 *
 * Set by the garage rather than the supplier, so they arrive on the channel and
 * apply across sizes. Deliberately simple: a minimum quantity and either a
 * percentage or a fixed amount off each tyre, optionally narrowed to brands or
 * a range. Anything more elaborate belongs in a promotions engine, not here.
 */

export interface TyrePromotion {
  id: string
  /** Customer-facing wording, e.g. "Buy 2 or more, save 10%". */
  label: string
  minQuantity: number
  percentOff?: number | null
  amountOffPerTyre?: number | null
  /** Limits the offer to these brands. Empty or absent means any brand. */
  brands?: string[] | null
  /** Limits the offer to these ranges. */
  tiers?: TyreTier[] | null
}

export interface PromotionResult {
  promotion: TyrePromotion
  /** Total before the discount. */
  originalTotal: number
  /** Total after the discount. */
  discountedTotal: number
  /** How much the customer saves. Always positive. */
  saving: number
}

/** A promotion the customer would qualify for by adding a tyre or two. */
export interface PromotionNudge {
  promotion: TyrePromotion
  /** How many more tyres are needed. */
  additionalQuantity: number
  /** What they would save at that quantity. */
  savingAtThreshold: number
}

function isUsable(promotion: TyrePromotion): boolean {
  if (!promotion || !Number.isFinite(promotion.minQuantity) || promotion.minQuantity < 1) return false

  const percent = normalizePrice(promotion.percentOff)
  const perTyre = normalizePrice(promotion.amountOffPerTyre)

  const hasPercent = percent != null && percent > 0 && percent <= 100
  const hasAmount = perTyre != null && perTyre > 0

  return hasPercent || hasAmount
}

/** True when the promotion's brand and range limits allow this tyre. */
export function appliesToOffer(promotion: TyrePromotion, offer: TyreOffer): boolean {
  if (promotion.brands?.length && !promotion.brands.includes(offer.brand)) return false
  if (promotion.tiers?.length && !(offer.tier && promotion.tiers.includes(offer.tier))) return false
  return true
}

function discountFor(promotion: TyrePromotion, offer: TyreOffer, quantity: number): number {
  const percent = normalizePrice(promotion.percentOff)
  if (percent != null && percent > 0) {
    return round(offerTotal(offer, quantity) * (Math.min(percent, 100) / 100))
  }

  const perTyre = normalizePrice(promotion.amountOffPerTyre)
  if (perTyre != null && perTyre > 0) {
    // Never discount below zero, however generous the rule.
    return round(Math.min(perTyre * quantity, offerTotal(offer, quantity)))
  }

  return 0
}

function round(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * The best promotion for this tyre at this quantity, or null.
 *
 * "Best" is the largest saving — a customer should never be quietly given the
 * worse of two offers they qualify for.
 */
export function bestPromotion(
  promotions: TyrePromotion[],
  offer: TyreOffer,
  quantity: number
): PromotionResult | null {
  if (!Array.isArray(promotions) || quantity < 1) return null

  let best: PromotionResult | null = null

  for (const promotion of promotions) {
    if (!isUsable(promotion)) continue
    if (quantity < promotion.minQuantity) continue
    if (!appliesToOffer(promotion, offer)) continue

    const originalTotal = offerTotal(offer, quantity)
    const saving = discountFor(promotion, offer, quantity)
    if (saving <= 0) continue

    if (!best || saving > best.saving) {
      best = {
        promotion,
        originalTotal,
        discountedTotal: round(originalTotal - saving),
        saving,
      }
    }
  }

  return best
}

/**
 * The cheapest way to unlock a better offer, or null.
 *
 * This is the nudge that grows the order: "add one more tyre and save £28". Only
 * suggests a threshold the customer is genuinely close to — within two tyres —
 * because "buy 3 more" is not a nudge, it is a different purchase.
 */
export function promotionNudge(
  promotions: TyrePromotion[],
  offer: TyreOffer,
  quantity: number,
  maxQuantity = 4
): PromotionNudge | null {
  if (!Array.isArray(promotions) || quantity < 1) return null

  const current = bestPromotion(promotions, offer, quantity)
  const currentSaving = current?.saving ?? 0

  let best: PromotionNudge | null = null

  for (const promotion of promotions) {
    if (!isUsable(promotion)) continue
    if (promotion.minQuantity <= quantity) continue
    if (promotion.minQuantity > maxQuantity) continue
    if (!appliesToOffer(promotion, offer)) continue

    const additionalQuantity = promotion.minQuantity - quantity
    if (additionalQuantity > 2) continue

    const savingAtThreshold = discountFor(promotion, offer, promotion.minQuantity)
    if (savingAtThreshold <= currentSaving) continue

    // Prefer the nearest threshold; break ties on the bigger saving.
    const better =
      !best ||
      additionalQuantity < best.additionalQuantity ||
      (additionalQuantity === best.additionalQuantity && savingAtThreshold > best.savingAtThreshold)

    if (better) {
      best = { promotion, additionalQuantity, savingAtThreshold }
    }
  }

  return best
}

/** Short badge wording for a card, e.g. "Save £28.40". */
export function describeSaving(result: PromotionResult): string {
  return `Save £${result.saving % 1 === 0 ? result.saving : result.saving.toFixed(2)}`
}
